import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FiLock, FiMail, FiShield, FiTrendingUp } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearAuthError,
  login,
  selectAuth,
  selectIsAuthenticated,
} from "../../redux/auth/authSlice";

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { isLoading } = useSelector(selectAuth);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
  });

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [from, isAuthenticated, navigate]);

  useEffect(() => () => dispatch(clearAuthError()), [dispatch]);

  const onSubmit = async (data) => {
    try {
      await dispatch(login(data)).unwrap();
      toast.success("Signed in successfully.");
    } catch (message) {
      toast.error(typeof message === "string" ? message : "Login failed.");
    }
  };

  return (
    <main className="min-h-screen bg-base-200 font-sans antialiased">
      <section className="grid min-h-screen lg:grid-cols-2">
        <div className="relative flex flex-col justify-center overflow-hidden border-b border-base-300 bg-base-100 px-6 py-12 lg:border-b-0 lg:border-r lg:px-14 lg:py-16">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/5" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-secondary/5" />

          <div className="relative mx-auto w-full max-w-xl">
            <div className="ui-kicker-soft mb-2 inline-flex items-center gap-2 rounded-full border border-base-300 bg-base-200/80 px-4 py-1.5">
              <span className="h-2 w-2 rounded-full bg-primary" />
              Builders workspace
            </div>
            <h1 className="ui-display-title mt-6">
              Smarter pipeline. Clearer decisions.
            </h1>
            <p className="ui-lead mt-4 max-w-lg">
              One place for leads, deals, and teamwork—built for teams who need
              clarity without clutter.
            </p>

            <div className="mt-10 space-y-3">
              <div className="flex gap-4 rounded-2xl border border-base-300/80 bg-base-200/50 p-4 backdrop-blur-sm transition hover:border-primary/30">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <FiTrendingUp className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <p className="ui-name-strong">Pipeline</p>
                  <p className="ui-muted mt-1.5 max-w-sm">
                    Stage deals and forecast with confidence.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 rounded-2xl border border-base-300/80 bg-base-200/50 p-4 backdrop-blur-sm transition hover:border-primary/30">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <FiShield className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <p className="ui-name-strong">Security</p>
                  <p className="ui-muted mt-1.5 max-w-sm">
                    Role-based access for every module.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center px-6 py-12 lg:px-12 lg:py-16">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h2 className="ui-page-title">Sign in</h2>
              <p className="ui-muted mt-2">
                Enter your work email and password to continue.
              </p>
            </div>

            <form
              className="space-y-5"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className="form-control w-full">
                <label htmlFor="login-email" className="label pb-1.5">
                  <span className="ui-label-form">Email</span>
                </label>
                <label
                  className={`input input-bordered flex h-11 items-center gap-3 rounded-lg border bg-base-100 pl-3 pr-3 ${
                    errors.email
                      ? "border-error focus-within:outline-error"
                      : "border-base-300"
                  }`}
                >
                  <FiMail
                    className="h-4 w-4 shrink-0 text-base-content/40"
                    aria-hidden
                  />
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    className="ui-input-text grow border-0 bg-transparent outline-none"
                    placeholder="you@company.com"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Enter a valid email address",
                      },
                    })}
                  />
                </label>
                {errors.email ? (
                  <p className="ui-error-text mt-1.5" role="alert">
                    {errors.email.message}
                  </p>
                ) : null}
              </div>

              <div className="form-control w-full">
                <label htmlFor="login-password" className="label pb-1.5">
                  <span className="ui-label-form">Password</span>
                </label>
                <label
                  className={`input input-bordered flex h-11 items-center gap-3 rounded-lg border bg-base-100 pl-3 pr-3 ${
                    errors.password
                      ? "border-error focus-within:outline-error"
                      : "border-base-300"
                  }`}
                >
                  <FiLock
                    className="h-4 w-4 shrink-0 text-base-content/40"
                    aria-hidden
                  />
                  <input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    className="ui-input-text grow border-0 bg-transparent outline-none"
                    placeholder="••••••••"
                    {...register("password", {
                      required: "Password is required",
                    })}
                  />
                </label>
                {errors.password ? (
                  <p className="ui-error-text mt-1.5" role="alert">
                    {errors.password.message}
                  </p>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`btn btn-primary h-11 w-full rounded-lg text-sm font-medium shadow-sm ${
                  isLoading ? "btn-disabled" : ""
                }`}
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            <p className="ui-footnote mt-8">
              Protected by your organization&apos;s policies.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

export default LoginPage;
